/**
 * Patch 35.3b — Campaign Completion Checklist.
 *
 * Renders the canonical `progress` block from the pack summary. Never
 * hardcodes state — every stage entry comes straight from the
 * backend. Archived assets, superseded versions, and specifications
 * are excluded upstream in ``AssetLifecycleService.pack_summary``.
 */
import { Check, Circle, Loader2 } from "lucide-react";
import type { PackProgress, StageEntry } from "@/entities/ai-studio";

interface Props {
  progress: PackProgress;
  currentStage: string | null | undefined;
  lastCompletedStage: string | null | undefined;
}

const STAGES: Array<{ key: keyof PackProgress; label: string }> = [
  { key: "campaign_setup", label: "Campaign setup" },
  { key: "strategy_generated", label: "Strategy generated" },
  { key: "copy_reviewed", label: "Copy reviewed" },
  { key: "image_prompts_reviewed", label: "Image prompts reviewed" },
  { key: "images_rendered", label: "Images rendered" },
  { key: "final_media_approved", label: "Final media approved" },
  { key: "vaulted", label: "Stored in Vault" },
  { key: "scheduled", label: "Scheduled" },
  { key: "published", label: "Published" },
];

function stateIcon(state: string, isCurrent: boolean) {
  if (state === "complete") return <Check className="h-4 w-4 text-emerald-500" />;
  if (state === "active" && isCurrent) return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
  if (state === "active") return <Circle className="h-4 w-4 text-primary" strokeWidth={2} />;
  return <Circle className="h-4 w-4 text-muted-foreground/50" />;
}

function stateLabel(entry: StageEntry): string {
  if (entry.state === "complete") return "Complete";
  if (entry.state === "not_started") return "Not started";
  const c = entry.complete ?? 0;
  const t = entry.total ?? 0;
  if (t > 0) return `${c} of ${t}`;
  return "In progress";
}

export function CampaignChecklist({
  progress, currentStage, lastCompletedStage,
}: Props): JSX.Element {
  return (
    <section
      data-testid="campaign-checklist"
      className="rounded-md border border-border/60 bg-background/40 p-3 text-xs"
    >
      <div className="mb-2 flex items-baseline justify-between">
        <div className="font-semibold uppercase tracking-widest text-muted-foreground">
          Campaign progress
        </div>
        {lastCompletedStage && (
          <div
            data-testid="campaign-checklist-last-completed"
            className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400"
          >
            Just done · {STAGES.find((s) => s.key === lastCompletedStage)?.label}
          </div>
        )}
      </div>
      <ul className="space-y-1">
        {STAGES.map(({ key, label }) => {
          const entry = progress[key];
          const isCurrent = currentStage === key;
          return (
            <li
              key={key}
              data-testid={`campaign-checklist-${key}`}
              data-state={entry.state}
              data-current={isCurrent ? "true" : "false"}
              className={
                "flex items-center gap-2 rounded px-1 py-0.5 " +
                (isCurrent ? "bg-primary/5 font-medium" : "")
              }
            >
              {stateIcon(entry.state, isCurrent)}
              <span className="flex-1">{label}</span>
              <span
                data-testid={`campaign-checklist-${key}-value`}
                className={
                  "font-mono text-[10px] " +
                  (entry.state === "complete"
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-muted-foreground")
                }
              >
                {stateLabel(entry)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
